import { useEffect, useState } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery } from "urql"
import { toast } from "sonner"
import { usePostHog } from "@posthog/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { sourceSchema, type SourceFormValues } from "@/lib/schemas"
import { CreateSourceMutation, UpdateSourceMutation } from "@/lib/graphql/mutations"
import { ProvidersQuery } from "@/lib/graphql/queries"

interface SourceFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  initialData?: {
    id: string
    name: string
    slug: string
    providerType: string
    verifyConfig?: Record<string, unknown> | null
    rateLimitRps: number
    spikeProtection: boolean
    maxIngestRps: number
    status: string
    brokerConfig?: Record<string, unknown> | null
    responseConfig?: { statusCode: number; body: string; contentType: string } | null
    dedupConfig?: { strategy: string; fields?: string[] | null; window?: string | null } | null
  }
}

function extractBrokerDefaults(brokerConfig?: Record<string, unknown> | null) {
  if (!brokerConfig || !brokerConfig.type) {
    return { brokerType: "none" as const, brokerQueueUrl: "", brokerRegion: "", brokerMaxMessages: 10, brokerWaitTimeSeconds: 20 }
  }
  return {
    brokerType: (brokerConfig.type as "sqs") ?? "none",
    brokerQueueUrl: (brokerConfig.queue_url as string) ?? "",
    brokerRegion: (brokerConfig.region as string) ?? "",
    brokerMaxMessages: (brokerConfig.max_messages as number) ?? 10,
    brokerWaitTimeSeconds: (brokerConfig.wait_time_seconds as number) ?? 20,
  }
}

function buildBrokerConfig(values: SourceFormValues): Record<string, unknown> | undefined {
  if (!values.brokerType || values.brokerType === "none") return undefined
  const config: Record<string, unknown> = { type: values.brokerType }
  if (values.brokerType === "sqs") {
    config.queue_url = values.brokerQueueUrl
    if (values.brokerRegion) config.region = values.brokerRegion
    if (values.brokerMaxMessages && values.brokerMaxMessages !== 10) config.max_messages = values.brokerMaxMessages
    if (values.brokerWaitTimeSeconds !== undefined && values.brokerWaitTimeSeconds !== 20) config.wait_time_seconds = values.brokerWaitTimeSeconds
  }
  return config
}

export function SourceForm({ open, onOpenChange, onSuccess, initialData }: SourceFormProps) {
  const posthog = usePostHog()
  const isEdit = !!initialData
  const [, createSource] = useMutation(CreateSourceMutation)
  const [, updateSource] = useMutation(UpdateSourceMutation)
  const [providersResult] = useQuery({ query: ProvidersQuery })
  const providers = providersResult.data?.providers ?? []
  const [showResponseConfig, setShowResponseConfig] = useState(!!initialData?.responseConfig)

  const brokerDefaults = extractBrokerDefaults(initialData?.brokerConfig)

  const form = useForm<SourceFormValues>({
    resolver: zodResolver(sourceSchema) as Resolver<SourceFormValues>,
    defaultValues: {
      name: initialData?.name ?? "",
      slug: initialData?.slug ?? "",
      providerType: (initialData?.providerType as string) ?? "generic",
      verifyConfig: initialData?.verifyConfig
        ? JSON.stringify(initialData.verifyConfig, null, 2)
        : "",
      rateLimitRps: initialData?.rateLimitRps ?? 0,
      spikeProtection: initialData?.spikeProtection ?? false,
      maxIngestRps: initialData?.maxIngestRps ?? 0,
      status: (initialData?.status as SourceFormValues["status"]) ?? "ACTIVE",
      ...brokerDefaults,
      responseStatusCode: initialData?.responseConfig?.statusCode ?? undefined,
      responseBody: initialData?.responseConfig?.body ?? "",
      responseContentType: initialData?.responseConfig?.contentType ?? "",
      dedupStrategy: (initialData?.dedupConfig?.strategy as SourceFormValues["dedupStrategy"]) ?? "none",
      dedupFields: initialData?.dedupConfig?.fields?.join(", ") ?? "",
      dedupWindow: initialData?.dedupConfig?.window ?? "",
    },
  })

  const brokerType = form.watch("brokerType")
  const spikeProtection = form.watch("spikeProtection")
  const dedupStrategy = form.watch("dedupStrategy")

  useEffect(() => {
    if (open) {
      const bd = extractBrokerDefaults(initialData?.brokerConfig)
      setShowResponseConfig(!!initialData?.responseConfig)
      form.reset({
        name: initialData?.name ?? "",
        slug: initialData?.slug ?? "",
        providerType: (initialData?.providerType as string) ?? "generic",
        verifyConfig: initialData?.verifyConfig
          ? JSON.stringify(initialData.verifyConfig, null, 2)
          : "",
        rateLimitRps: initialData?.rateLimitRps ?? 0,
        spikeProtection: initialData?.spikeProtection ?? false,
        maxIngestRps: initialData?.maxIngestRps ?? 0,
        status: (initialData?.status as SourceFormValues["status"]) ?? "ACTIVE",
        ...bd,
        responseStatusCode: initialData?.responseConfig?.statusCode ?? undefined,
        responseBody: initialData?.responseConfig?.body ?? "",
        responseContentType: initialData?.responseConfig?.contentType ?? "",
        dedupStrategy: (initialData?.dedupConfig?.strategy as SourceFormValues["dedupStrategy"]) ?? "none",
        dedupFields: initialData?.dedupConfig?.fields?.join(", ") ?? "",
        dedupWindow: initialData?.dedupConfig?.window ?? "",
      })
    }
  }, [open, initialData, form])

  const onSubmit = async (values: SourceFormValues) => {
    let verifyConfig: Record<string, unknown> | undefined
    if (values.verifyConfig) {
      try {
        verifyConfig = JSON.parse(values.verifyConfig) as Record<string, unknown>
      } catch {
        form.setError("verifyConfig", { message: "Invalid JSON" })
        return
      }
    }

    const brokerConfig = buildBrokerConfig(values)

    const responseConfig = showResponseConfig ? {
      statusCode: values.responseStatusCode ?? 200,
      body: values.responseBody ?? "",
      contentType: values.responseContentType ?? "application/json",
    } : undefined

    const dedupConfig = values.dedupStrategy && values.dedupStrategy !== "none" ? {
      strategy: values.dedupStrategy,
      fields: values.dedupStrategy === "field_based" && values.dedupFields
        ? values.dedupFields.split(",").map((s) => s.trim()).filter(Boolean)
        : undefined,
      window: values.dedupWindow || undefined,
    } : undefined

    if (isEdit) {
      const result = await updateSource({
        id: initialData.id,
        input: {
          name: values.name,
          slug: values.slug,
          providerType: values.providerType,
          verifyConfig,
          rateLimitRps: values.rateLimitRps,
          spikeProtection: values.spikeProtection,
          maxIngestRps: values.maxIngestRps,
          status: values.status,
          brokerConfig,
          responseConfig,
          dedupConfig,
        },
      })
      if (result.error) {
        toast.error(result.error.message)
        return
      }
      posthog?.capture("source_updated", { provider_type: values.providerType, slug: values.slug })
      toast.success("Source updated")
    } else {
      const result = await createSource({
        input: {
          name: values.name,
          slug: values.slug,
          providerType: values.providerType,
          verifyConfig,
          rateLimitRps: values.rateLimitRps,
          spikeProtection: values.spikeProtection,
          maxIngestRps: values.maxIngestRps,
          status: values.status,
          brokerConfig,
          responseConfig,
          dedupConfig,
        },
      })
      if (result.error) {
        toast.error(result.error.message)
        return
      }
      posthog?.capture("source_created", { provider_type: values.providerType, slug: values.slug })
      toast.success("Source created")
    }

    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Source" : "Create Source"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" {...form.register("name")} />
              <FieldError error={form.formState.errors.name} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input id="slug" className="font-mono" {...form.register("slug")} />
              <FieldError error={form.formState.errors.slug} />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.watch("status") ?? "ACTIVE"}
                onValueChange={(v) => form.setValue("status", v as SourceFormValues["status"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select
                  value={form.watch("providerType")}
                  onValueChange={(v) => form.setValue("providerType", v as string)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rateLimitRps">Rate Limit (RPS)</Label>
                <Input
                  id="rateLimitRps"
                  type="number"
                  {...form.register("rateLimitRps")}
                />
                <FieldError error={form.formState.errors.rateLimitRps} />
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="spikeProtection">Spike Protection</Label>
                <p className="text-xs text-muted-foreground leading-relaxed max-w-sm">
                  When enabled, events are always accepted during traffic spikes instead of being rejected with 429. Delivery is smoothed by per-destination rate limiting.
                </p>
              </div>
              <Switch
                id="spikeProtection"
                checked={spikeProtection ?? false}
                onCheckedChange={(checked) => form.setValue("spikeProtection", checked)}
              />
            </div>

            {spikeProtection && (
              <div className="space-y-2">
                <Label htmlFor="maxIngestRps">Warning Threshold (RPS)</Label>
                <Input
                  id="maxIngestRps"
                  type="number"
                  placeholder="0 = unlimited"
                  {...form.register("maxIngestRps")}
                />
                <p className="text-xs text-muted-foreground">0 = unlimited</p>
                <FieldError error={form.formState.errors.maxIngestRps} />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="verifyConfig">Verify Config (JSON)</Label>
            <Textarea
              id="verifyConfig"
              className="font-mono text-xs min-h-20"
              placeholder='{"secret": "whsec_..."}'
              {...form.register("verifyConfig")}
            />
            <FieldError error={form.formState.errors.verifyConfig} />
          </div>

          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label>Message Broker</Label>
              <Select
                value={brokerType ?? "none"}
                onValueChange={(v) => form.setValue("brokerType", v as SourceFormValues["brokerType"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (HTTP only)</SelectItem>
                  <SelectItem value="sqs">Amazon SQS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {brokerType === "sqs" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="brokerQueueUrl">Queue URL</Label>
                  <Input
                    id="brokerQueueUrl"
                    className="font-mono text-xs"
                    placeholder="https://sqs.us-east-1.amazonaws.com/123456789/my-queue"
                    {...form.register("brokerQueueUrl")}
                  />
                  <FieldError error={form.formState.errors.brokerQueueUrl} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brokerRegion">Region</Label>
                  <Input
                    id="brokerRegion"
                    placeholder="us-east-1"
                    {...form.register("brokerRegion")}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="brokerMaxMessages">Max Messages</Label>
                    <Input
                      id="brokerMaxMessages"
                      type="number"
                      {...form.register("brokerMaxMessages")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="brokerWaitTimeSeconds">Wait Time (s)</Label>
                    <Input
                      id="brokerWaitTimeSeconds"
                      type="number"
                      {...form.register("brokerWaitTimeSeconds")}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label>Custom Response</Label>
                <p className="text-xs text-muted-foreground">Override the default response sent to the webhook provider</p>
              </div>
              <Switch
                checked={showResponseConfig}
                onCheckedChange={setShowResponseConfig}
              />
            </div>
            {showResponseConfig && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="responseStatusCode">Status Code</Label>
                    <Input id="responseStatusCode" type="number" placeholder="200" {...form.register("responseStatusCode")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="responseContentType">Content-Type</Label>
                    <Input id="responseContentType" placeholder="application/json" {...form.register("responseContentType")} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="responseBody">Body</Label>
                  <Textarea id="responseBody" className="font-mono text-xs min-h-15" placeholder='{"status": "ok"}' {...form.register("responseBody")} />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4 rounded-lg glass-card p-4">
            <Label className="text-xs font-medium">Deduplication</Label>
            <div className="space-y-2">
              <Label>Strategy</Label>
              <Select value={dedupStrategy ?? "none"} onValueChange={(v) => form.setValue("dedupStrategy", v as SourceFormValues["dedupStrategy"])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="exact">Exact (idempotency key)</SelectItem>
                  <SelectItem value="field_based">Field-based</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {dedupStrategy === "field_based" && (
              <div className="space-y-2">
                <Label htmlFor="dedupFields">Fields (comma-separated)</Label>
                <Input id="dedupFields" className="font-mono text-sm" placeholder="$.data.id, $.data.type" {...form.register("dedupFields")} />
              </div>
            )}
            {dedupStrategy && dedupStrategy !== "none" && (
              <div className="space-y-2">
                <Label htmlFor="dedupWindow">Window</Label>
                <Input id="dedupWindow" className="font-mono" placeholder="1h" {...form.register("dedupWindow")} />
                <p className="text-xs text-muted-foreground">Duration for dedup check (e.g. 1h, 30m)</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
